import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setSuccessOpen(true);
    } catch (error) {
      toast.error("Error", {
        description: "Failed to send message. Please try WhatsApp instead.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#1E3A5F] mb-4">Get In Touch</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out via WhatsApp, email, or the form below.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Cards */}
          <div className="lg:col-span-1 space-y-6">
            {/* WhatsApp Card */}
            <Card className="border-2 border-[#FF8C42] bg-gradient-to-br from-[#FF8C42]/5 to-white hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#FF8C42] rounded-lg">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1E3A5F] mb-2">WhatsApp</h3>
                    <p className="text-gray-600 text-sm mb-3">Fast response, usually within minutes</p>
                    <Button
                      onClick={() => window.open('https://wa.me/610487698671', '_blank')}
                      className="w-full bg-[#FF8C42] hover:bg-[#e67a35] text-white"
                    >
                      Chat Now
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="border-2 border-gray-200 hover:border-[#1E3A5F] hover:shadow-lg transition-all">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#1E3A5F] rounded-lg">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1E3A5F] mb-2">Phone</h3>
                    <p className="text-gray-600 text-sm mb-1">Call us directly</p>
                    <a
                      href="tel:+610487698671"
                      onClick={() => trackEvent('click_call_button', { location: 'contact_section' })}
                      className="text-[#FF8C42] font-semibold hover:underline">
                      +61 048 769 8671
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card className="border-2 border-gray-200 hover:border-[#1E3A5F] hover:shadow-lg transition-all">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#1E3A5F] rounded-lg">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1E3A5F] mb-2">Email</h3>
                    <p className="text-gray-600 text-sm mb-1">Send us an email</p>
                    <a
                      href="mailto:info@movematesydney.com"
                      onClick={() => trackEvent('click_email', { location: 'contact_section' })}
                      className="text-[#FF8C42] font-semibold hover:underline break-all">
                      info@movematesydney.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location Card */}
            <Card className="border-2 border-gray-200 hover:border-[#1E3A5F] hover:shadow-lg transition-all">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#1E3A5F] rounded-lg">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1E3A5F] mb-2">Service Area</h3>
                    <p className="text-gray-600 text-sm">
                      Sydney Metro, NSW Regional & Interstate
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-2 border-2 border-gray-200 shadow-lg">
            <CardContent className="pt-6">
              <h3 className="text-2xl font-bold text-[#1E3A5F] mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700 font-medium">Your Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => {
                        const onlyLetters = e.target.value.replace(/[^A-Za-zÀ-ÿ\s'-]/g, '');
                        setFormData({ ...formData, name: onlyLetters });
                      }}
                      required
                      minLength={2}
                      pattern="[A-Za-zÀ-ÿ\s'\-]+"
                      title="Letters only"
                      className="py-5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      placeholder="0412345678"
                      value={formData.phone}
                      onChange={(e) => {
                        const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setFormData({ ...formData, phone: onlyDigits });
                      }}
                      required
                      pattern="[0-9]{10}"
                      maxLength={10}
                      title="10 digits, numbers only"
                      className="py-5"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                    title="Please enter a valid email containing @"
                    className="py-5"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-700 font-medium">Your Message *</Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us about your moving needs..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={6}
                    className="resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-[#FF8C42] hover:bg-[#e67a35] text-white py-6 text-lg disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent className="sm:max-w-md" data-testid="contact-success-dialog">
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4 animate-in zoom-in duration-300">
              <CheckCircle2 className="w-12 h-12 text-green-600" strokeWidth={2} />
            </div>
            <DialogHeader>
              <DialogTitle className="text-2xl text-[#1E3A5F]">Message sent!</DialogTitle>
              <DialogDescription className="text-base mt-2">
                We've received your enquiry. Our team will get back to you shortly.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6 w-full">
              <Button
                onClick={() => setSuccessOpen(false)}
                data-testid="contact-success-close"
                className="w-full bg-[#FF8C42] hover:bg-[#e67a35] text-white"
              >
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};